import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { StripHtmlPipe } from 'app/shared/pipes/strip-html.pipe';
import { NotificationsService } from './notifications.service';
import { NotificationFilterModeEnum, type NotificationFilterMode, type Notification } from './notifications.types';
import {
  filterNotifications,
  formatNotificationCount,
  formatNotificationDate,
  formatNotificationTime,
  groupNotifications,
  isMessageNotification,
  resolveNotificationHref,
} from './notifications.util';

export const NotificationListVariantEnum = <const>{
  Page: 'page',
  Panel: 'panel',
};

export type NotificationListVariant = (typeof NotificationListVariantEnum)[keyof typeof NotificationListVariantEnum];

@Component({
  selector: 'app-notification-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FuseScrollbarDirective, MatProgressSpinner, StripHtmlPipe],
  template: `
    <div
      class="notification-list"
      [class.notification-list--page]="variant() === NotificationListVariantEnum.Page"
      [class.notification-list--panel]="variant() === NotificationListVariantEnum.Panel"
      data-cy="notification-list">
      <div class="notification-list__toolbar">
        <div class="notification-list__segments" role="group" aria-label="Notificaties filteren">
          @for (option of filterOptions(); track option.value) {
            <button
              type="button"
              class="notification-list__segment"
              [class.notification-list__segment--active]="filterMode() === option.value"
              [attr.aria-pressed]="filterMode() === option.value"
              [attr.data-cy]="'notification-filter-' + option.value"
              (click)="filterMode.set(option.value)">
              <span>{{ option.label }}</span>
              <span
                class="notification-list__counter"
                [class.notification-list__counter--capped]="option.count.endsWith('+')">
                {{ option.count }}
              </span>
            </button>
          }
        </div>

        @if (showMarkAllAsRead()) {
          <button
            type="button"
            class="notification-list__mark-all"
            aria-label="Alles als gelezen markeren"
            [disabled]="
              (!notificationsService.hasMoreEntries() && notificationsService.unreadCount() === 0) ||
              notificationsService.loading() ||
              notificationsService.markingAllAsRead()
            "
            (click)="notificationsService.markAllAsRead()"
            data-cy="notification-mark-all-read">
            <span
              class="notification-asset-icon notification-asset-icon--check-check notification-list__mark-all-icon"
              aria-hidden="true"></span>
            <span>Markeren als gelezen</span>
          </button>
        }
      </div>

      @if (showMarkAllAsRead() && notificationsService.markAllAsReadFailed()) {
        <div class="notification-list__mark-all-error" role="alert">
          Markeren mislukt. Probeer opnieuw.
        </div>
      }

      <div
        class="notification-list__scroll"
        #scrollContainer
        fuseScrollbar
        [attr.aria-busy]="notificationsService.loading() || notificationsService.markingAllAsRead()"
        (scroll)="notificationsService.loadMoreEntries($event)"
        data-cy="notification-scroll">
        @if (groups().length === 0 && notificationsService.loadMoreFailed()) {
          <div class="notification-list__state" role="alert">
            <strong>Notificaties konden niet worden geladen</strong>
            <button
              type="button"
              class="notification-list__retry"
              (click)="notificationsService.retryLoadMoreEntries()">
              Opnieuw proberen
            </button>
          </div>
        } @else if (
          groups().length === 0 && (notificationsService.loading() || notificationsService.hasMoreEntries())
        ) {
          <div class="notification-list__state">
            <mat-spinner diameter="28"></mat-spinner>
            <span>Notificaties laden…</span>
          </div>
        } @else if (groups().length === 0) {
          <div class="notification-list__state" data-cy="notification-empty-state">
            <span
              class="notification-asset-icon notification-asset-icon--bell notification-list__empty-icon"
              aria-hidden="true"></span>
            <strong>{{ emptyStateTitle() }}</strong>
            <span>{{ emptyStateDescription() }}</span>
          </div>
        } @else {
          @for (group of groups(); track group.label) {
            <section class="notification-list__group" [attr.aria-label]="group.label">
              @if (variant() === NotificationListVariantEnum.Page) {
                <h2 class="notification-list__group-label">{{ group.label }}</h2>
              } @else {
                <h3 class="notification-list__group-label">{{ group.label }}</h3>
              }

              @for (notification of group.notifications; track notification.id) {
                <article
                  class="notification-list__item"
                  [class.notification-list__item--unread]="!notification.entry.read"
                  [attr.data-cy]="'notification-item-' + notification.id">
                  <a
                    class="notification-list__content"
                    [attr.href]="notificationHref(notification)"
                    (click)="selectNotification($event, notification)"
                    [attr.aria-label]="
                      (notification.entry.read ? 'Gelezen notificatie: ' : 'Ongelezen notificatie: ') +
                      ((notification.entry.title | stripHtml) || 'Notificatie openen')
                    ">
                    <span class="notification-list__heading">
                      @if (!notification.entry.read) {
                        <span class="notification-list__unread-dot" aria-hidden="true"></span>
                      }
                      <span class="notification-list__title" [innerHTML]="notification.entry.title"></span>
                    </span>

                    @if (notification.entry.description) {
                      <span class="notification-list__description" [innerHTML]="notification.entry.description"></span>
                    }

                    <span class="notification-list__metadata">
                      @if (formatDate(notification); as notificationDate) {
                        <span>{{ notificationDate }}</span>
                      }
                      @if (formatTime(notification); as notificationTime) {
                        <span class="notification-list__separator" aria-hidden="true"></span>
                        <span>{{ notificationTime }}</span>
                      }
                      @if (isMessage(notification)) {
                        <span class="notification-list__separator" aria-hidden="true"></span>
                        <span>Bericht</span>
                      }
                    </span>
                  </a>

                  <button
                    type="button"
                    class="notification-list__read-action"
                    [disabled]="notificationsService.markingAllAsRead()"
                    (click)="toggleRead($event, notification)"
                    [attr.aria-label]="notification.entry.read ? 'Markeer als ongelezen' : 'Markeer als gelezen'"
                    [attr.data-cy]="'notification-toggle-read-' + notification.id">
                    <span
                      class="notification-asset-icon notification-list__read-icon"
                      [class.notification-asset-icon--mail]="notification.entry.read"
                      [class.notification-asset-icon--mail-open]="!notification.entry.read"
                      aria-hidden="true"></span>
                    <span>{{ notification.entry.read ? 'Markeer als ongelezen' : 'Markeer als gelezen' }}</span>
                  </button>
                </article>
              }
            </section>
          }
          @if (notificationsService.loadMoreFailed()) {
            <div class="notification-list__load-more-error" role="alert">
              <span>Meer notificaties laden is mislukt.</span>
              <button
                type="button"
                class="notification-list__retry"
                (click)="notificationsService.retryLoadMoreEntries()">
                Opnieuw proberen
              </button>
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      --notification-background: #ffffff;
      --notification-border: #eeeff1;
      --notification-card-shadow: rgba(18, 18, 18, 0.05);
      --notification-counter-border: #a9a9ad;
      --notification-fill: #000000;
      --notification-hover: rgba(0, 0, 0, 0.08);
      --notification-info: #172eff;
      --notification-invert: #ffffff;
      --notification-separator: #c7c7cc;
      --notification-secondary: #545559;
      --notification-surface-button: #ffffff;
      --notification-text: #000000;
      display: block;
      min-height: 0;
    }

    :host-context(.dark) {
      --notification-background: #1e1e1e;
      --notification-border: #2c2c2e;
      --notification-card-shadow: rgba(30, 30, 30, 0.35);
      --notification-counter-border: #545559;
      --notification-fill: #ffffff;
      --notification-hover: rgba(255, 255, 255, 0.15);
      --notification-info: #5ca8ff;
      --notification-invert: #000000;
      --notification-separator: #2c2c2e;
      --notification-secondary: #c7c7cc;
      --notification-surface-button: #000000;
      --notification-text: #ffffff;
    }

    .notification-list {
      color: var(--notification-text);
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .notification-list__toolbar {
      align-items: center;
      display: flex;
      flex: none;
      justify-content: space-between;
    }

    .notification-list--panel .notification-list__toolbar {
      background: var(--notification-background);
      min-height: 56px;
      padding: 12px;
    }

    .notification-list--page .notification-list__toolbar {
      gap: 24px;
      height: 44px;
    }

    .notification-list__segments {
      align-items: center;
      display: flex;
      gap: 6px;
    }

    .notification-list--panel .notification-list__segments {
      max-width: 100%;
      min-width: 0;
      overflow-x: auto;
    }

    .notification-list--page .notification-list__segments {
      background: var(--notification-background);
      border-radius: 12px;
      padding: 6px;
    }

    .notification-list__segment {
      align-items: center;
      background: transparent;
      border: 0;
      border-radius: 8px;
      color: var(--notification-secondary);
      cursor: pointer;
      display: flex;
      font-size: 12px;
      font-weight: 500;
      gap: 6px;
      height: 32px;
      justify-content: center;
      line-height: 1.4;
      padding: 0 10px;
      transition:
        background-color 120ms ease,
        color 120ms ease;
      white-space: nowrap;
    }

    .notification-list__segment:not(.notification-list__segment--active):hover {
      background: var(--notification-hover);
    }

    .notification-list__segment--active {
      background: var(--notification-fill);
      color: var(--notification-invert);
    }

    .notification-list__segment--active:hover,
    .notification-list__segment--active:focus-visible {
      background: var(--notification-fill);
      color: var(--notification-invert);
    }

    .notification-list__counter {
      align-items: center;
      background: var(--notification-background);
      border: 1px solid var(--notification-counter-border);
      border-radius: 9999px;
      color: var(--notification-secondary);
      display: flex;
      flex: none;
      font-size: 12px;
      font-variant-numeric: tabular-nums;
      font-weight: 500;
      height: 25px;
      justify-content: center;
      line-height: 1;
      padding: 0;
      width: 25px;
    }

    .notification-list__counter--capped {
      font-size: 10px;
    }

    .notification-list__mark-all {
      align-items: center;
      align-self: center;
      background: var(--notification-surface-button);
      border: 0;
      border-radius: 8px;
      box-shadow: 0 1px 2px rgba(18, 18, 18, 0.05);
      color: var(--notification-text);
      cursor: pointer;
      display: flex;
      font-size: 14px;
      font-weight: 500;
      gap: 4px;
      height: 40px;
      justify-content: center;
      line-height: 1.4;
      min-width: 230px;
      padding: 0 32px;
      transition: background-color 120ms ease;
    }

    .notification-list__mark-all:not(:disabled):hover {
      background: color-mix(in srgb, var(--notification-surface-button), var(--notification-text) 8%);
    }

    .notification-list__mark-all:disabled {
      cursor: default;
      opacity: 0.45;
    }

    .notification-list__mark-all-icon {
      height: 20px;
      width: 20px;
    }

    .notification-asset-icon {
      background: currentColor;
      display: inline-block;
      flex: none;
      mask-position: center;
      mask-repeat: no-repeat;
      mask-size: contain;
      -webkit-mask-position: center;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-size: contain;
    }

    .notification-asset-icon--bell {
      mask-image: url('/assets/icons/notifications/bell.svg');
      mask-size: 75% 83.333%;
      -webkit-mask-image: url('/assets/icons/notifications/bell.svg');
      -webkit-mask-size: 75% 83.333%;
    }

    .notification-asset-icon--check-check {
      mask-image: url('/assets/icons/notifications/check-check.svg');
      mask-size: 83.333% 48%;
      -webkit-mask-image: url('/assets/icons/notifications/check-check.svg');
      -webkit-mask-size: 83.333% 48%;
    }

    .notification-asset-icon--mail-open {
      mask-image: url('/assets/icons/notifications/mail-open.svg');
      mask-size: 83.333%;
      -webkit-mask-image: url('/assets/icons/notifications/mail-open.svg');
      -webkit-mask-size: 83.333%;
    }

    .notification-asset-icon--mail {
      mask-image: url('/assets/icons/notifications/mail.svg');
      mask-size: 83.333%;
      -webkit-mask-image: url('/assets/icons/notifications/mail.svg');
      -webkit-mask-size: 83.333%;
    }

    .notification-list__scroll {
      background: var(--notification-background);
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .notification-list--panel .notification-list__scroll {
      height: 365px;
      margin-top: 4px;
    }

    .notification-list--page .notification-list__scroll {
      border-radius: 12px;
      box-shadow: 0 1px 2px var(--notification-card-shadow);
      margin-top: 12px;
      max-height: calc(100vh - 246px);
      min-height: 360px;
    }

    .notification-list__group-label {
      color: var(--notification-secondary);
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      margin: 0;
    }

    .notification-list--panel .notification-list__group-label {
      padding: 8px 12px 6px;
    }

    .notification-list--page .notification-list__group-label {
      padding: 16px 16px 8px;
    }

    .notification-list__item {
      background: var(--notification-background);
      border-bottom: 1px solid var(--notification-border);
      display: flex;
      position: relative;
      transition: background-color 120ms ease;
    }

    .notification-list__item:hover,
    .notification-list__item:focus-within {
      background: var(--notification-hover);
    }

    .notification-list__content {
      align-items: stretch;
      background: transparent;
      border: 0;
      color: inherit;
      cursor: pointer;
      display: flex;
      flex: 1;
      flex-direction: column;
      min-width: 0;
      text-align: left;
      text-decoration: none;
    }

    .notification-list--panel .notification-list__content {
      gap: 2px;
      padding: 8px 12px 12px;
    }

    .notification-list--page .notification-list__content {
      gap: 4px;
      padding: 14px 16px;
    }

    .notification-list__heading {
      align-items: center;
      display: flex;
      gap: 8px;
      min-width: 0;
    }

    .notification-list__unread-dot {
      background: var(--notification-info);
      border-radius: 9999px;
      flex: 0 0 8px;
      height: 8px;
      width: 8px;
    }

    .notification-list__title {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
      min-width: 0;
    }

    .notification-list--page .notification-list__title {
      font-size: 15px;
    }

    .notification-list__description {
      color: var(--notification-secondary);
      display: -webkit-box;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
    }

    .notification-list--panel .notification-list__description {
      -webkit-line-clamp: 2;
    }

    .notification-list--page .notification-list__description {
      max-width: 730px;
      -webkit-line-clamp: 3;
    }

    .notification-list__metadata {
      align-items: center;
      color: var(--notification-secondary);
      display: flex;
      flex-wrap: wrap;
      font-size: 12px;
      gap: 4px;
      line-height: 1.5;
      min-height: 18px;
    }

    .notification-list--panel .notification-list__metadata {
      margin-top: 6px;
    }

    .notification-list--page .notification-list__metadata {
      margin-top: 8px;
    }

    .notification-list__separator {
      background: var(--notification-separator);
      border-radius: 9999px;
      height: 4px;
      margin: 0 2px;
      width: 4px;
    }

    .notification-list__read-action {
      align-items: center;
      align-self: flex-start;
      background: var(--notification-background);
      border: 1px solid var(--notification-border);
      border-radius: 8px;
      color: var(--notification-text);
      cursor: pointer;
      display: flex;
      font-size: 12px;
      font-weight: 500;
      gap: 6px;
      justify-content: center;
      line-height: 1;
      white-space: nowrap;
    }

    .notification-list--panel .notification-list__read-action {
      bottom: 12px;
      font-size: 10px;
      gap: 4px;
      opacity: 0;
      padding: 4px 8px 5px;
      pointer-events: none;
      position: absolute;
      right: 8px;
      transition: opacity 120ms ease;
    }

    .notification-list--panel .notification-list__item:hover .notification-list__read-action,
    .notification-list--panel .notification-list__item:focus-within .notification-list__read-action {
      opacity: 1;
      pointer-events: auto;
    }

    .notification-list--page .notification-list__read-action {
      margin: 14px 16px 0 8px;
      padding: 4px 8px;
    }

    .notification-list__read-action:hover {
      background: var(--notification-hover);
    }

    .notification-list__read-icon {
      height: 14px;
      width: 14px;
    }

    .notification-list--page .notification-asset-icon--mail-open {
      height: 16px;
      width: 16px;
    }

    .notification-list--panel .notification-list__read-icon {
      height: 10px;
      width: 10px;
    }

    .notification-list__state {
      align-items: center;
      color: var(--notification-secondary);
      display: flex;
      flex-direction: column;
      gap: 8px;
      justify-content: center;
      min-height: 240px;
      padding: 32px;
      text-align: center;
    }

    .notification-list__state strong {
      color: var(--notification-text);
      font-size: 15px;
      font-weight: 500;
    }

    .notification-list__load-more-error {
      align-items: center;
      color: var(--notification-secondary);
      display: flex;
      font-size: 12px;
      gap: 8px;
      justify-content: center;
      padding: 12px;
    }

    .notification-list__retry {
      background: transparent;
      border: 0;
      border-bottom: 1px solid currentColor;
      color: var(--notification-text);
      cursor: pointer;
      font: inherit;
      font-weight: 500;
      padding: 0;
    }

    .notification-list__mark-all-error {
      color: #d42e11;
      font-size: 12px;
      padding: 0 12px 8px;
    }

    :host-context(.dark) .notification-list__mark-all-error {
      color: #ff7a61;
    }

    .notification-list__empty-icon {
      height: 32px;
      width: 32px;
    }

    @media (max-width: 639px) {
      .notification-list--page .notification-list__toolbar {
        align-items: stretch;
        flex-direction: column;
        height: auto;
      }

      .notification-list--page .notification-list__segments {
        overflow-x: auto;
      }

      .notification-list--page .notification-list__mark-all {
        width: 100%;
      }

      .notification-list--page .notification-list__scroll {
        max-height: calc(100vh - 310px);
      }

      .notification-list--page .notification-list__read-action {
        margin-right: 8px;
      }

      .notification-list--panel .notification-list__read-action {
        opacity: 1;
        pointer-events: auto;
      }
    }
  `,
})
export class NotificationListComponent {
  @ViewChild('scrollContainer', { read: ElementRef })
  private scrollContainer?: ElementRef<HTMLElement>;

  protected readonly notificationsService = inject(NotificationsService);
  protected readonly NotificationListVariantEnum = NotificationListVariantEnum;

  readonly variant = input<NotificationListVariant>(NotificationListVariantEnum.Panel);
  readonly showMarkAllAsRead = input(false);
  readonly selected = output<Notification>();

  protected readonly filterMode = signal<NotificationFilterMode>(NotificationFilterModeEnum.All);
  protected readonly filterOptions = computed(() => [
    {
      value: NotificationFilterModeEnum.All,
      label: 'Alles',
      count: this.formatCount(this.notificationsService.entries().length),
    },
    {
      value: NotificationFilterModeEnum.Messages,
      label: 'Berichten',
      count: this.formatCount(this.notificationsService.messageCount()),
    },
    {
      value: NotificationFilterModeEnum.Unread,
      label: 'Ongelezen',
      count: formatNotificationCount(
        this.notificationsService.unreadCount(),
        this.notificationsService.hasMoreUnreadEntries()
      ),
    },
  ]);
  protected readonly groups = computed(() =>
    groupNotifications(filterNotifications(this.notificationsService.entries(), this.filterMode()))
  );
  protected readonly emptyStateTitle = computed(() =>
    this.filterMode() === NotificationFilterModeEnum.Unread ? 'Geen ongelezen notificaties' : 'Geen notificaties'
  );
  protected readonly emptyStateDescription = computed(() =>
    this.filterMode() === NotificationFilterModeEnum.All
      ? 'Nieuwe notificaties verschijnen hier.'
      : 'Kies een ander filter om meer notificaties te bekijken.'
  );

  constructor() {
    effect(() => {
      this.groups();
      const loading = this.notificationsService.loading();
      const hasMoreEntries = this.notificationsService.hasMoreEntries();
      if (!loading && hasMoreEntries) {
        setTimeout(() => this.loadNextPageWhenListDoesNotScroll());
      }
    });
  }

  protected isMessage(notification: Notification): boolean {
    return isMessageNotification(notification);
  }

  protected formatDate(notification: Notification): string {
    return formatNotificationDate(notification.entry.time);
  }

  protected formatTime(notification: Notification): string {
    return formatNotificationTime(notification.entry.time);
  }

  protected toggleRead(event: MouseEvent, notification: Notification): void {
    event.stopPropagation();
    if (this.notificationsService.markingAllAsRead()) {
      return;
    }
    this.notificationsService.toggleRead(notification);
  }

  protected notificationHref(notification: Notification): string {
    const slug = this.notificationsService.entity()?.slug;
    return resolveNotificationHref(
      notification.entry.link,
      window.location.origin,
      slug ? `/${slug}` : '/notificaties'
    );
  }

  protected selectNotification(event: MouseEvent, notification: Notification): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.selected.emit(notification);
  }

  private formatCount(count: number): string {
    return formatNotificationCount(count, this.notificationsService.hasMoreEntries());
  }

  private loadNextPageWhenListDoesNotScroll(): void {
    const scrollElement = this.scrollContainer?.nativeElement;
    if (!scrollElement || this.notificationsService.loading() || !this.notificationsService.hasMoreEntries()) {
      return;
    }

    if (this.groups().length === 0 || scrollElement.scrollHeight <= scrollElement.clientHeight + 2) {
      this.notificationsService.loadNextPage();
    }
  }
}
